import React from 'react';

import { useNarrativeTextInput } from '../hooks/useNarrativeTextInput';
import { logger } from '../lib/logger';

const choiceLogger = logger.child('[ChoiceInput]');

interface ChoiceInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  maxLength?: number;
}

export const ChoiceInput = React.memo(function ChoiceInput({
  value,
  onChange,
  placeholder,
  className,
  maxLength,
}: ChoiceInputProps) {
  const {
    value: localValue,
    onChange: handleChange,
    error,
  } = useNarrativeTextInput(value, onChange, 300, maxLength);

  // Calculate dynamic rows based on content length (max 3 lines)
  const lines = localValue.split('\n').length;
  const estimatedLines = Math.ceil(localValue.length / 60); // Rough estimate: 60 chars per line
  const dynamicRows = Math.min(3, Math.max(1, Math.max(lines, estimatedLines)));

  choiceLogger.debug('ChoiceInput render', { value, localValue, placeholder, dynamicRows });

  return (
    <>
      <textarea
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className={`${className} resize-none overflow-hidden`}
        rows={dynamicRows}
        style={{
          minHeight: '2.5rem', // 1 line minimum
          maxHeight: '4.5rem', // 3 lines maximum
        }}
      />
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </>
  );
});

interface ChoiceTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
  maxLength?: number;
}

export const ChoiceTextarea = React.memo(function ChoiceTextarea({
  value,
  onChange,
  placeholder,
  className,
  rows = 2,
  maxLength,
}: ChoiceTextareaProps) {
  const {
    value: localValue,
    onChange: handleChange,
    error,
  } = useNarrativeTextInput(value, onChange, 300, maxLength);

  choiceLogger.debug('ChoiceTextarea render', { value, localValue, placeholder });

  return (
    <>
      <textarea
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className={className}
        rows={rows}
      />
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </>
  );
});
